<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import StatusBadge from '@/components/utilities/StatusBadge.vue';
import Tooltip from '@/components/utilities/Tooltip.vue';
import BuildingRenderCanvasAccessible from '@/components/maps_canvases/BuildingRenderCanvasAccessible.vue';
import { useFloor } from '@/composables/useFloor.ts';
import { formatCurrencyWithoutDecimal } from '@/helpers/currencyHelper.ts';
import { getImageUrlWithCustomWidth } from '@/helpers/imageHelpers.ts';
import { formatAvailabilityDate } from '@/helpers/dateHelpers.ts';
import type { LocaleSchema } from '@/locales/index.ts';
import type { Unit } from '@/typings/units/Unit.ts';
import type { BuildingFacade } from '@/typings/buildings/BuildingFacade';
import type { FacadeRenderLayer } from '@/typings/drawing/FacadeRenderLayer';
import { getUnitPositionText } from '@/enums/UnitPosition';
import { getUnitStatusString, isUnitDisabled, UnitStatusEnum } from '@/enums/UnitStatus';

const { t, locale } = useI18n<{ message: LocaleSchema }>({
  useScope: 'global',
});
const { getFloorTextByNumber } = useFloor();

const props = defineProps<{
  unit: Unit;
  href: string;
  mode?: 'rental' | 'purchase';
  unitsSearch?: boolean;
  buildingFacades?: BuildingFacade[];
}>();

const emit = defineEmits<{
  click: [unit: Unit];
}>();

const unitIsDisabled = computed(() => isUnitDisabled(props.unit.status));

function handleClick(event: Event) {
  if (!props.href) {
    event.preventDefault();
    emit('click', props.unit);
  }
}

const imageIsLoading = ref(true);
const currentImageIndex = ref(0);

// Variables pour le swipe tactile
const touchStartX = ref(0);
const touchEndX = ref(0);
const isTouch = ref(false);
const minSwipeDistance = 50;

const getUnitPrice = computed(() => {
  if (props.unit.basePrice === null) {
    return t('common.notAvailableAbbreviated');
  }
  const price = formatCurrencyWithoutDecimal(props.unit.basePrice, locale);
  if (props.mode === 'rental') {
    
    return `${price} / ${t('common.month').toLowerCase()}`;
  }
  return price;
});

const facadeWithUnit = computed(() => {
  if (!props.buildingFacades || props.buildingFacades.length === 0) {
    return undefined;
  }
  
  const sortedFacades = [...props.buildingFacades].sort((a, b) => a.facadeOrder - b.facadeOrder);
  
  return sortedFacades.find(facade =>
    facade.facadeUnits.some(facadeUnit =>
      facadeUnit.unitId === props.unit.id &&
      facadeUnit.canvasPolygonData &&
      facadeUnit.canvasPolygonData.trim() !== ''
    )
  );
});

const unitFacadeLayer = computed<FacadeRenderLayer | null>(() => {
  const facade = facadeWithUnit.value;
  if (!facade) return null;
  
  const facadeUnit = facade.facadeUnits.find(fu => fu.unitId === props.unit.id);
  if (!facadeUnit?.canvasPolygonData) return null;
  
  try {
    const polygons = JSON.parse(facadeUnit.canvasPolygonData);
    return {
      id: props.unit.id,
      unitId: props.unit.id,
      unitNumber: props.unit.unitNumber,
      floorName: props.unit.floorNo,
      status: props.unit.status,
      basePrice: props.unit.basePrice,
      bedrooms: props.unit.bedrooms,
      bathrooms: props.unit.bathrooms,
      dens: props.unit.dens,
      polygons: polygons
    } as FacadeRenderLayer;
  } catch (error) {
    console.error('Error parsing polygon data for unit:', props.unit.id, error);
    return null;
  }
});

const availableImages = computed(() => {
  const images = [];

  // D'abord essayer l'image du bâtiment de l'unité, sinon utiliser l'image de la façade
  let buildingImageUrl = props.unit.buildingImageUrl;
  if (!buildingImageUrl && facadeWithUnit.value?.buildingImageUrl) {
    buildingImageUrl = facadeWithUnit.value.buildingImageUrl;
  }
  
  if (buildingImageUrl) {
    images.push(getImageUrlWithCustomWidth(buildingImageUrl, 500));
  }

  if (props.unit.images && props.unit.images.length > 0) {
    // Take first 4 images
    const imagesToAdd = props.unit.images.slice(0, 4);
    imagesToAdd.forEach(image => {
      images.push(getImageUrlWithCustomWidth(image.imageUrl, 500));
    });
  }

  return images;
});

const currentImage = computed(() => {
  return availableImages.value[currentImageIndex.value];
});

const hasMultipleImages = computed(() => {
  return availableImages.value.length > 1;
});

// Adresse de l'unité avec ville
const unitAddress = computed(() => {
  const addressParts = [];
  if (props.unit.buildingNo) {
    addressParts.push(props.unit.buildingNo);
  }
  if (props.unit.localizedStreet) {
    addressParts.push(props.unit.localizedStreet);
  }
  
  const streetAddress = addressParts.join(' ');
  const city = props.unit.city;
  
  if (streetAddress && city) {
    return `${streetAddress}, ${city}`;
  } else if (streetAddress) {
    return streetAddress;
  } else if (city) {
    return city;
  }
  
  return t('common.addressNotAvailable');
});

const unitPositionText = computed(() => getUnitPositionText(props.unit.position, t));

const unitConfigurationNames = computed(() => {
  const isEnglish = locale.value.toLowerCase().includes('en');
  
  if (!props.unit.unitUnitConfigurations || props.unit.unitUnitConfigurations.length === 0) {
    return [];
  }
  
  return props.unit.unitUnitConfigurations
    .map(config => {
      const name = isEnglish ? config.unitConfigurationNameEn : config.unitConfigurationName;
      return name || '';
    })
    .filter(name => name.length > 0);
});

function selectImage(index: number) {
  currentImageIndex.value = index;
  imageIsLoading.value = true;
}

function unitHasPromotion(unit: Unit): boolean {
  // Une unité louée ou vendue garde souvent le texte de promotion de son bâtiment : pas de badge dans ce cas
  if (unit.status === UnitStatusEnum.Rented || unit.status === UnitStatusEnum.Sold) {
    return false;
  }

  const localeValue = locale.value.toLowerCase();
 
  return Boolean((localeValue === 'en-ca' && (unit.promotionsEn?.trim()?.length ?? 0) > 0)
         || (localeValue === 'fr-ca' && (unit.promotions?.trim()?.length ?? 0) > 0));
}

function getPromotionText(unit: Unit): string {
  const localeValue = locale.value.toLowerCase();
  
  if (localeValue === 'en-ca') {
    return unit.promotionsEn?.trim() || '';
  }
  
  return unit.promotions?.trim() || '';
}

// Fonctions de navigation du carousel
function goToPrevious() {
  if (!hasMultipleImages.value) return;
  currentImageIndex.value = currentImageIndex.value === 0 
    ? availableImages.value.length - 1 
    : currentImageIndex.value - 1;
  imageIsLoading.value = true;
}

function goToNext() {
  if (!hasMultipleImages.value) return;
  currentImageIndex.value = currentImageIndex.value === availableImages.value.length - 1 
    ? 0 
    : currentImageIndex.value + 1;
  imageIsLoading.value = true;
}

// Fonctions de gestion du swipe tactile
function handleTouchStart(event: TouchEvent) {
  if (!hasMultipleImages.value) return;
  
  isTouch.value = true;
  touchStartX.value = event.touches[0].clientX;
  event.stopPropagation(); // Empêche la propagation vers le lien parent
}

function handleTouchMove(event: TouchEvent) {
  if (!isTouch.value || !hasMultipleImages.value) return;
  
  const currentX = event.touches[0].clientX;
  const deltaX = Math.abs(currentX - touchStartX.value);
  
  // Si le mouvement horizontal est significatif, empêcher le scroll
  if (deltaX > 10) {
    event.preventDefault();
    event.stopPropagation();
  }
}

function handleTouchEnd(event: TouchEvent) {
  if (!isTouch.value || !hasMultipleImages.value) return;
  
  touchEndX.value = event.changedTouches[0].clientX;
  const deltaX = touchStartX.value - touchEndX.value;
  
  if (Math.abs(deltaX) > minSwipeDistance) {
    event.preventDefault(); // Empêche la navigation du lien
    event.stopPropagation();
    
    if (deltaX > 0) {
      // Swipe vers la gauche - image suivante
      goToNext();
    } else {
      // Swipe vers la droite - image précédente
      goToPrevious();
    }
  }
  
  isTouch.value = false;
}
</script>

<template>
  <div class="cardTopImage-container">
    <component :is="href ? 'a' : 'div'"
               :href="href || undefined"
               @click="handleClick">
      <div class="cardTopImage cardTopImage_unitLinkCard"
           :data-unit-status="unit.status">
        <div>
          <div class="cardTopImage-headerContainer">
            <h3 class="cardTopImage-heading">
              {{ t('browsingApp.units.unitNumber') }} {{ unit.unitNumber }}
            </h3>
            
            <div class="cardTopImage-statusContainer">
              <Tooltip v-if="unitHasPromotion(unit)"
                       :text="getPromotionText(unit)"
                       position="top">
                <div class="tagPromotion">{{ t('unitSummary.promotion') }}</div>
              </Tooltip>
              <StatusBadge class="unitLinkCard-statusBadge"
                           :status="getUnitStatusString(unit.status)"
                           :label="t(`browsingApp.statuses.unitStatus.${getUnitStatusString(unit.status)}`)" />
            </div>
          </div>
          <div class="cardTopImage-imageContainer">
            <div v-if="availableImages.length > 0"
                 class="cardTopImage-carousel"
                 @touchstart="handleTouchStart"
                 @touchmove="handleTouchMove"
                 @touchend="handleTouchEnd">
              <img :src="currentImage"
                   alt=""
                   class="cardTopImage-image"
                   :data-image-is-loading="imageIsLoading"
                   @load="imageIsLoading = false" />
              <div v-if="hasMultipleImages"
                   class="cardTopImage-carouselDots"
                   @click.prevent.stop>
                <button v-for="(image, index) in availableImages"
                        :key="index"
                        type="button"
                        class="cardTopImage-carouselDot"
                        :class="{ 'cardTopImage-carouselDot--active': index === currentImageIndex }"
                        :aria-label="`Image ${index + 1}`"
                        @click.prevent.stop="selectImage(index)">
                </button>
              </div>
            </div>
            <div v-else-if="facadeWithUnit && unitFacadeLayer"
                 class="cardTopImage-interactivePlan">
              <BuildingRenderCanvasAccessible :layers="[unitFacadeLayer]"
                                              :image-url="facadeWithUnit.buildingImageUrl"
                                              :hide-onboarding-tooltip="true"
                                              class="cardTopImage-facadeCanvas">
                <template #tooltip="{ layer }">
                  <div v-if="layer && layer.unitNumber">
                    {{ `${t('browsingApp.units.unitNumber')} ${layer.unitNumber}` }}
                  </div>
                </template>
              </BuildingRenderCanvasAccessible>
            </div>
            <div v-else
                 class="cardTopImage-noImage">
              {{ t('common.noImageAvailable') }}
            </div>
          </div>
          <div class="cardTopImage-contentContainer">
            <div v-if="props.unitsSearch" class="address">
              {{ unitAddress }}
            </div>
            <dl class="unitLinkCard-data">
              <div v-if="unitConfigurationNames.length > 0" class="unitLinkCard-dataGroup unitLinkCard-roomConfig">
                <dt>{{ t('browsingApp.units.size') }}</dt>
                <dd>{{ unitConfigurationNames.join(', ') }}</dd>
              </div>
              <div v-if="unit.floorNo !== undefined" class="unitLinkCard-dataGroup unitLinkCard-floor">
                <dt>{{ t('browsingApp.units.floor') }}</dt>
                <dd>{{ getFloorTextByNumber(unit.floorNo) }}</dd>
              </div>
              <div v-if="unitPositionText" class="unitLinkCard-dataGroup unitLinkCard-position">
                <dt>{{ t('browsingApp.units.position') }}</dt>
                <dd>{{ unitPositionText }}</dd>
              </div>
              <div v-if="unit.livingArea" class="unitLinkCard-dataGroup unitLinkCard-surface">
                <dt>{{ t('browsingApp.units.livingArea') }}</dt>
                <dd>{{ unit.livingArea }} {{ t('browsingApp.units.squareFeet') }}</dd>
              </div>
              <div v-if="unit.availabilityDate" class="unitLinkCard-dataGroup unitLinkCard-availability">
                <dt>
                  <template v-if="props.mode === 'purchase'">
                    {{ t('browsingApp.units.closestAvailabilityDate') }}
                  </template>
                  <template v-else>
                    {{ t('browsingApp.units.movingDate') }}
                  </template>
                </dt>
                <dd>{{ formatAvailabilityDate(unit.availabilityDate, locale, t, props.mode, unit.status) }}</dd>
              </div>
              <div class="unitLinkCard-dataGroup unitLinkCard-price">
                <dt>
                  <template v-if="props.mode === 'purchase'">
                    {{ t('browsingApp.units.startingPrice') }}
                  </template>
                  <template v-else>
                    {{ t('browsingApp.units.price') }}
                  </template>
                </dt>
                <dd>{{ getUnitPrice }}</dd>
              </div>

              <div v-if="$slots['extra-info']" class="unitLinkCard-extraInfo">
                <slot name="extra-info">
                </slot>
              </div>
            </dl>
          </div>
          <div v-if="$slots.footer"
               class="unitLinkCard-footer">
            <slot name="footer">
            </slot>
          </div>
        </div>
        <span v-if="href" class="unitLinkCard-viewAction">
          {{ t('browsingApp.units.viewUnit') }}
          <span class="unitLinkCard-arrow">
            <svg width="17"
                height="14"
                viewBox="0 0 17 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
              <path d="M9.60179 0.0012207L8.32797 1.25257L12.9042 5.74763H0.832031V7.51625H12.9042L8.32797 12.0122L9.60179 13.2631L16.3518 6.63216L9.60179 0.0012207Z"
                    fill="currentColor" />
            </svg>
          </span>
        </span>
      </div>
    </component>
  </div>
</template>

<style lang="scss" scoped>

.unitLinkList-unitCard{
  a{
    text-decoration: none;
    color:var(--gray-950);
    display:block;
    height:100%;

    &:hover:not(.cardTopImage-link--disabled){
      .cardTopImage_unitLinkCard{
        outline-color: var(--primary-500);
        outline-offset: -1px;
      }
      .unitLinkCard-arrow {
        transform: translateX(2px);
      }
    }
    
    &.cardTopImage-link--disabled {
      pointer-events: none;
      cursor: not-allowed;
    }

    .cardTopImage {
      height:100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
  }
  
  div.cardTopImage-link--disabled {
    pointer-events: none;
    cursor: not-allowed;
  }
}

.cardTopImage-container {
  overflow: visible;
}

.cardTopImage_unitLinkCard {
  border: 2px solid var(--gray-200);
  outline: 4px solid transparent;
  outline-offset: -4px;
  border-radius: .8rem;
  background-color:#ffffff;
  padding: 3.2rem;
  overflow: visible;
  
  &[data-disabled="true"] {
    opacity: 0.6;
    pointer-events: none;
  }

  .cardTopImage-headerContainer {
    display: flex;
    align-items: baseline;
    gap: 1.6rem;
    margin-bottom: 1.6rem;
    justify-content: space-between;
    flex-wrap: wrap;
    overflow: visible;
  }
  .cardTopImage-statusContainer {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 0.8rem;
    overflow: visible;
  }

  .cardTopImage-heading {
    font-size: 2rem;
    font-weight: 600;
    font-family: "Inter", "Roboto", sans-serif;
    margin: 0;
    text-decoration: none;
  }

  .cardTopImage-titleGroup {
    margin: 0;
  }

  .cardTopImage-contentContainer {
    padding: 1.6rem 0 0 0;
    display: block;

    .address {
      font-size: 1.6rem;
      font-weight: 700;
    }
  }

  .cardTopImage-locationGroup {
    margin: 2.2rem 0 4.2rem;
  }

  .cardTopImage-regionName {
    font-size: 1.8rem;
    font-weight: 600;
    margin-bottom: 0.125rem;
  }

  .cardTopImage-subheading {
    font-size: 1.6rem;
    font-weight: 500;
  }

  .cardTopImage-carousel {
    position: relative;
    touch-action: pan-y;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }

  .cardTopImage-image {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
    border-radius: 8px;
    pointer-events: none;
  }

  .cardTopImage-interactivePlan {
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid var(--gray-200);
  }

  .cardTopImage-facadeCanvas {
    width: 100%;
    height: 100%;
  }

  .cardTopImage-noImage {
    width: 100%;
    height:100%;
    display:flex;
    justify-content: center;
    align-items: center;
    border: 1px solid var(--gray-200);
    border-radius: 8px;
    height: auto;
    aspect-ratio: 16 / 9;
  }

  .cardTopImage-carouselDots {
    position: absolute;
    bottom: 1.2rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 0.8rem;
    background: rgba(0, 0, 0, 0.5);
    padding: 0.6rem 1.2rem;
    border-radius: 2rem;
  }

  .cardTopImage-carouselDot {
    width: 0.8rem;
    height: 0.8rem;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.6);
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.8);
      transform: scale(1.1);
    }
    
    &.cardTopImage-carouselDot--active {
      background: white;
      transform: scale(1.2);
    }
  }

  // Status colors
  &[data-unit-status="Rented"] {
    --baseStatusColor: var(--red-500);
    --statusBgColor: var(--red-200);
  }

  &[data-unit-status="Available"] {
    --baseStatusColor: var(--green-500);
    --statusBgColor: var(--green-200);
  }

  &[data-unit-status="Reserved"] {
    --baseStatusColor: var(--orange-500);
    --statusBgColor: var(--orange-200);
  }

  &[data-unit-status="Upcoming"] {
    --baseStatusColor: var(--yellow-500);
    --statusBgColor: var(--yellow-200);
  }

  .unitLinkCard-data {
    margin: 1.6rem 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.6rem;

    .unitLinkCard-dataGroup {
      dt {
        font-size: 1.4rem;
      }

      dd {
        font-size: 1.6rem;
        font-weight: 700;
        margin: 0;
      }
    }
  }

  .unitLinkCard-viewAction {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
    font-size: 1.25rem;
    font-weight: 500;
    color: var(--primary-600);
    text-decoration: none;
  }

  .unitLinkCard-arrow {
    font-size: 1.25rem;
    transition: transform 0.2s;
  }
}

@media screen and (max-width: 384px){
  .cardTopImage_unitLinkCard{
    padding:1.6rem;
    
     .cardTopImage-headerContainer{
      flex-direction: column;
     }
  }
}
</style>
